import { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Flex, Spin } from "antd"
import CollapsibleSection from "ui/CollapsibleSection"
import styles from "../../index.module.css"

// Localized fallback labels for known fields when the backend omits `label`.
const FIELD_LABEL_KEY = {
  guests: "booking.reservation_detail.history_guests",
  total: "booking.reservation_detail.history_total",
  date: "booking.reservation_detail.history_date"
}

// Server-paginated booking change-history (GET /guide/bookings/{id}/change-history). Each entry
// is one edit grouping one or more field changes under an actor + timestamp; "Show more" fetches
// the next page. Crew/equipment/add-ons are added/removed rather than edited, so a null old/new
// value carries meaning (see renderChangeText); a both-null change is a marker (booking_created).
const BookingHistoryPanel = ({
  history = [],
  loading = false,
  hasMore = false,
  totalCount,
  onLoadMore,
  collapsible = false
}) => {
  const { t } = useTranslation()
  const scrollRef = useRef(null)
  // Index of the first row a "Show more" is about to append; used to scroll it into view + flash it.
  const pendingAnchorRef = useRef(null)
  const [highlightFrom, setHighlightFrom] = useState(null)

  const handleLoadMore = () => {
    pendingAnchorRef.current = history.length
    onLoadMore?.()
  }

  // After the next page lands, bring the first new row into view (scoped to the scroll box, never
  // the page) and briefly highlight the appended rows so it's obvious records were added.
  useEffect(() => {
    const anchor = pendingAnchorRef.current
    if (anchor == null || history.length <= anchor) return
    pendingAnchorRef.current = null

    const container = scrollRef.current
    const target = container?.querySelector(`[data-history-index="${anchor}"]`)
    if (container && target) {
      const top = target.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop
      container.scrollTo({ top, behavior: "smooth" })
    }
    setHighlightFrom(anchor)
    const timer = setTimeout(() => setHighlightFrom(null), 1600)
    return () => clearTimeout(timer)
  }, [history.length])

  const changeLabel = (change) => {
    if (change.label) return change.label
    const key = FIELD_LABEL_KEY[change.field]
    return key ? t(key) : change.field
  }

  // Crew changes carry a role — append it in brackets after the name (e.g. "sanjeev Saini (Crew Member)").
  const withRole = (change, value) => (change.roleName ? `${value} (${change.roleName})` : value)

  const renderChangeText = (change) => {
    const label = changeLabel(change)
    const hasFrom = change.from != null
    const hasTo = change.to != null
    // Both null → a labelled marker (e.g. "Booking Created"), not a from→to diff.
    if (!hasFrom && !hasTo) return <span className={styles.historyChangeLabel}>{label}</span>
    if (!hasFrom) return <>{t("booking.reservation_detail.history_added", { label, value: withRole(change, change.to) })}</>
    if (!hasTo) return <>{t("booking.reservation_detail.history_removed", { label, value: withRole(change, change.from) })}</>
    return (
      <>
        <span className={styles.historyChangeLabel}>{label}: </span>
        {change.from} → {change.to}
      </>
    )
  }

  const bodyContent = (() => {
    if (loading && history.length === 0) {
      return (
        <Flex justify="center" className={styles.panelLoading}>
          <Spin size="small" />
        </Flex>
      )
    }
    if (history.length === 0) {
      return <span className={styles.historyMeta}>{t("booking.reservation_detail.no_booking_history")}</span>
    }
    return (
      <Flex vertical>
        <Flex vertical ref={scrollRef} className={styles.historyScroll}>
          {history.map((entry, index) => (
            <div
              key={entry.id}
              data-history-index={index}
              className={`${styles.historyItem} ${highlightFrom != null && index >= highlightFrom ? styles.historyItemNew : ""}`}
            >
              {entry.changes.map((change, changeIndex) => (
                <span key={`${entry.id}-${change.field ?? change.changeType ?? changeIndex}`} className={styles.historyChange}>
                  {renderChangeText(change)}
                  {change.note ? <span className={styles.historyNote}>{change.note}</span> : null}
                </span>
              ))}
              {entry.author || entry.dateTime ? (
                <span className={styles.historyMeta}>{[entry.author, entry.dateTime].filter(Boolean).join(" • ")}</span>
              ) : null}
            </div>
          ))}
        </Flex>
        {hasMore && (
          <button type="button" className={styles.historyShowMore} onClick={handleLoadMore} disabled={loading}>
            {loading ? <Spin size="small" /> : t("booking.reservation_detail.history_show_more")}
          </button>
        )}
      </Flex>
    )
  })()

  if (collapsible) {
    return (
      <CollapsibleSection
        title={t("booking.reservation_detail.booking_history")}
        count={totalCount ?? history.length}
        className={styles.commPanel}
      >
        {bodyContent}
      </CollapsibleSection>
    )
  }

  return (
    <Flex vertical gap={12} className={styles.commPanel}>
      <span className={styles.commPanelTitle}>{t("booking.reservation_detail.booking_history")}</span>
      {bodyContent}
    </Flex>
  )
}

BookingHistoryPanel.propTypes = {
  history: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      changes: PropTypes.arrayOf(
        PropTypes.shape({
          changeType: PropTypes.string,
          field: PropTypes.string,
          label: PropTypes.string,
          from: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          to: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          roleName: PropTypes.string,
          note: PropTypes.string
        })
      ).isRequired,
      author: PropTypes.string,
      dateTime: PropTypes.string
    })
  ),
  loading: PropTypes.bool,
  hasMore: PropTypes.bool,
  totalCount: PropTypes.number,
  onLoadMore: PropTypes.func,
  collapsible: PropTypes.bool
}

export default BookingHistoryPanel
